import { useState } from 'react';
import { toast } from 'sonner';

import { useAdminPlans } from '@/shared/api/admin-plans';
import { type AdminUpgradeRequest, useGrantUpgradeRequest } from '@/shared/api/admin-upgrade-requests';
import { cn } from '@/shared/lib/cn';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

const PERIODS: Array<{ key: string; label: string; days: number | null }> = [
  { key: '30', label: '1 month', days: 30 },
  { key: '90', label: '3 months', days: 90 },
  { key: '365', label: '1 year', days: 365 },
  { key: 'life', label: 'No end date', days: null },
];

/**
 * Approve = grant a plan for a period (manual subscription, provider "manual")
 * and mark the request granted.  The plan defaults to the one requested; the
 * operator may pick another (e.g. Team for a team that asked for Enterprise).
 */
export function ApproveDialog(props: { request: AdminUpgradeRequest; label: string; onClose: () => void }) {
  const r = props.request;
  const { data: plans } = useAdminPlans();
  const grant = useGrantUpgradeRequest();
  const [planCode, setPlanCode] = useState(r.requestedPlanCode);
  const [period, setPeriod] = useState('365');
  const [customDays, setCustomDays] = useState('');

  const eligible = (plans ?? []).filter(
    (p) =>
      p.isActive &&
      p.code !== 'free' &&
      (r.customerType === 'user' ? p.code.startsWith('personal') : !p.code.startsWith('personal')),
  );
  if (!eligible.some((p) => p.code === r.requestedPlanCode)) {
    eligible.unshift({ code: r.requestedPlanCode, name: r.requestedPlanCode } as (typeof eligible)[number]);
  }

  const preset = PERIODS.find((p) => p.key === period);
  const days = period === 'custom' ? Number(customDays) : (preset?.days ?? null);
  const customInvalid = period === 'custom' && (!Number.isInteger(days) || (days ?? 0) < 1 || (days ?? 0) > 3650);
  const until =
    days && !customInvalid
      ? new Date(Date.now() + days * 86_400_000).toLocaleDateString(undefined, { dateStyle: 'medium' })
      : null;

  return (
    <ReasonDialog
      open
      onOpenChange={(o) => !o && props.onClose()}
      title="Approve upgrade request"
      description={`Grants a plan to ${props.label} right away and marks the request approved. The customer is not emailed automatically.`}
      confirmLabel={until ? `Approve until ${until}` : 'Approve without end date'}
      reasonLabel="Note"
      placeholder="e.g. Paid by bank transfer, invoice #123"
      pending={grant.isPending}
      onConfirm={async (reason) => {
        if (customInvalid) {
          toast.error('Custom period must be 1–3650 days');
          throw new Error('invalid_days');
        }
        await grant.mutateAsync({
          id: r.id,
          durationDays: days,
          planCode: planCode === r.requestedPlanCode ? undefined : planCode,
          reason,
        });
        toast.success(`Approved — ${planCode} is active for ${props.label}`);
        props.onClose();
      }}
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-neutral-400">Plan</span>
          <select
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
            className="w-full rounded-md bg-neutral-800 px-3 py-2 text-base text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500 sm:text-sm"
          >
            {eligible.map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} ({p.code}){p.code === r.requestedPlanCode ? ' — requested' : ''}
              </option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend className="mb-1 text-xs uppercase tracking-wider text-neutral-400">Period</legend>
          <div className="flex flex-wrap gap-1.5">
            {[...PERIODS, { key: 'custom', label: 'Custom…', days: null }].map((p) => (
              <button
                key={p.key}
                type="button"
                aria-pressed={period === p.key}
                onClick={() => setPeriod(p.key)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs ring-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
                  period === p.key
                    ? 'bg-orange-500/20 text-orange-200 ring-orange-500/50'
                    : 'text-neutral-300 ring-neutral-700 hover:bg-neutral-800',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === 'custom' ? (
            <input
              type="number"
              min={1}
              max={3650}
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              placeholder="days"
              aria-label="Custom period in days"
              className="mt-2 w-28 rounded-md bg-neutral-800 px-3 py-1.5 text-base text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500 sm:text-sm"
            />
          ) : null}
        </fieldset>
        <p className="text-[11px] text-neutral-500">
          The note is saved on the request (the customer’s billing API can read it) and in the audit log.
        </p>
      </div>
    </ReasonDialog>
  );
}
